import { InformationCircleIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  DeliveryMethod,
  LimitedOrderDocument,
  StoreOrderState,
  useEditEnteredQuoteQuery,
  useUpdateEnteredQuoteMutation,
} from '../_gen/gql'
import DocumentUpload from '../components/DocumentUpload'
import Frame from '../components/Frame'
import useGqlClient from '../hooks/useGqlClient'
import useUpload from './CreateExternalOrder/useUpload'

import Action from '@/gf/components/Action'
import Card from '@/gf/components/Card'
import Field from '@/gf/components/Field'
import Form from '@/gf/components/Form'
import InStockCheckbox from '@/gf/components/InStockCheckbox'
import Layout from '@/gf/components/Layout'
import Page from '@/gf/components/Page'
import RedAlert from '@/gf/components/RedAlert'
import DateInput from '@/gf/components/inputs/DateNext'
import PriceInput from '@/gf/components/inputs/PriceV2'
import QuantityInput from '@/gf/components/inputs/Quantity'
import MoneyM from '@/gf/modules/Money'
import type { Money } from '@/types'

type Line = {
  id: string
  mpn: string
  name: string
  quantity: number | null
  unitPrice: Money | null
  inStock: boolean
  availableAt: DateTime | null
}

const zeroPrice = MoneyM.fromDecimal(0, 'USD')

// A buyer corrects a quote they keyed in through Enter Quote (gf-xb8a9). Only the
// fields a buyer can get wrong when typing a vendor's quote in: quantity, unit price,
// availability, and the vendor's PDF. Lines are not added or removed here.
const EditEnteredQuote = () => {
  const { storeOrderId } = useParams() as { storeOrderId: string }
  const navigate = useNavigate()
  const client = useGqlClient()
  const [lines, setLines] = useState<Line[] | null>(null)
  const [error, setError] = useState<string>()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null)

  const { data } = useEditEnteredQuoteQuery({
    variables: { storeOrderFilter: JSON.stringify(['id_eq', storeOrderId]) },
    client,
    fetchPolicy: 'network-only',
    onCompleted: ({ storeOrder }) => {
      if (!storeOrder) return

      setLines(
        storeOrder.lineItems
          .filter((li) => !li.rejectedAt)
          .map((li) => ({
            id: li.id,
            mpn: li.product.mpn ?? '',
            name: li.product.name ?? '',
            quantity: li.quantity,
            unitPrice: li.unitPrice,
            inStock: !!li.inStock,
            availableAt: li.availableAt ?? null,
          }))
      )
    },
  })

  const [updateEnteredQuote, { loading }] = useUpdateEnteredQuoteMutation({
    client,
    refetchQueries: [LimitedOrderDocument],
  })

  // The PDF is only attached here; the buyer corrects the numbers by hand.
  const { uploadProcessing, uploadStarted, resetUpload, extractOrderDoc, documentId } = useUpload(
    () => undefined,
    { storageKey: `edit-entered-quote-doc-extraction-id:${storeOrderId}` }
  )

  const storeOrder = data?.storeOrder
  const orderUrl = `/orders/${storeOrderId}`

  const updateLine = (id: string, updates: Partial<Line>) =>
    setLines((current) => current && current.map((l) => (l.id === id ? { ...l, ...updates } : l)))

  const onSubmit = async () => {
    if (!lines) return
    setError(undefined)

    if (lines.some((l) => !l.quantity || l.quantity < 1)) {
      setError('Quantity must be at least 1 on every part.')
      return
    }

    if (lines.some((l) => !l.unitPrice)) {
      setError('Unit price is required on every part.')
      return
    }

    // Submitting mid-read would drop the PDF the buyer just attached.
    if (uploadProcessing) {
      setError('Still reading the uploaded quote. Give it a moment.')
      return
    }

    try {
      await updateEnteredQuote({
        variables: {
          storeOrderId,
          documentId,
          lineItems: lines.map((l) => ({
            id: l.id,
            quantity: l.quantity as number,
            unitPrice: l.unitPrice as Money,
            inStock: l.inStock,
            availableAt: !l.inStock && l.availableAt ? l.availableAt.toISODate() : null,
          })),
        },
      })

      resetUpload()
      navigate(orderUrl)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update the quote')
    }
  }

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Orders', href: '/orders' },
      { name: `Order ${storeOrder?.shortId ?? ''}`, href: orderUrl },
      { name: 'Edit Quote', href: `${orderUrl}/edit-quote` },
    ],
  }

  const editable =
    !!storeOrder && storeOrder.enteredByBuyer && storeOrder.state === StoreOrderState.OpenQuote

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Edit Quote" previousPage={orderUrl}>
        {storeOrder && !editable && (
          <p className="text-gray-600">
            Only a quote you entered yourself can be edited, and only before it is approved.
          </p>
        )}

        {editable && lines && (
          <Form onSubmit={onSubmit}>
            <Layout>
              <Layout.Section type="primary">
                <Card>
                  <Card.Section>
                    <div className="flex flex-row gap-x-1 items-center text-sm text-gray-700">
                      <InformationCircleIcon className="w-5 h-5 flex shrink-0" />
                      Correct the quote you entered from {storeOrder.store.name}. The vendor is not
                      notified.
                    </div>
                  </Card.Section>

                  <Card.Section>
                    <div className={lines.length > 1 ? 'divide-y' : ''}>
                      {lines.map((line) => (
                        <div key={line.id} className="flex flex-col gap-y-3 py-6 first:pt-0">
                          <div className="text-sm">
                            <span className="font-medium">{line.mpn}</span> {line.name}
                          </div>

                          <div className="flex gap-x-4 gap-y-2 flex-wrap">
                            <Field label="Quantity" htmlFor={`${line.id}-quantity`}>
                              <div className="w-24">
                                <QuantityInput
                                  value={line.quantity}
                                  setValue={(quantity) => updateLine(line.id, { quantity })}
                                  min={1}
                                  id={`${line.id}-quantity`}
                                />
                              </div>
                            </Field>

                            <Field label="Unit Price" htmlFor={`${line.id}-unit-price`}>
                              <div className="w-32">
                                <PriceInput
                                  price={line.unitPrice}
                                  onChange={(unitPrice) =>
                                    updateLine(line.id, { unitPrice: unitPrice ?? null })
                                  }
                                  min={0}
                                  id={`${line.id}-unit-price`}
                                />
                              </div>
                            </Field>

                            <Field label="Price">
                              <div className="text-base leading-4 py-2.5">
                                {MoneyM.format(
                                  MoneyM.mult(line.unitPrice || zeroPrice, line.quantity || 1)
                                )}
                              </div>
                            </Field>
                          </div>

                          <div className="flex gap-x-4 gap-y-2 flex-wrap">
                            <Field htmlFor={`${line.id}-in-stock`}>
                              <InStockCheckbox
                                className={classNames(
                                  'whitespace-nowrap',
                                  line.inStock ? 'mt-2' : 'mt-8'
                                )}
                                checked={line.inStock}
                                onChange={(e) => updateLine(line.id, { inStock: e.target.checked })}
                                deliveryMethod={
                                  storeOrder.deliveryMethod ?? DeliveryMethod.Shipping
                                }
                              />
                            </Field>

                            {!line.inStock && (
                              <Field label="Available at" htmlFor={`${line.id}-available`}>
                                <DateInput
                                  value={line.availableAt}
                                  onChange={(availableAt) =>
                                    updateLine(line.id, { availableAt: availableAt ?? null })
                                  }
                                  id={`${line.id}-available`}
                                />
                              </Field>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Section>
                </Card>
              </Layout.Section>

              <Layout.Section type="secondary">
                <Card>
                  <Card.Section className="space-y-4">
                    <DocumentUpload
                      title="Attach or Replace Quote PDF"
                      uploadContext="buyer_create_quote"
                      processing={!!uploadProcessing}
                      processingDescription="This may take up to a minute."
                      documentUrl={documentUrl}
                      documentPreviewUrl={documentPreviewUrl}
                      onStarted={uploadStarted}
                      onComplete={(url, previewUrl) => {
                        setDocumentUrl(url)
                        setDocumentPreviewUrl(previewUrl)
                        extractOrderDoc(url)
                      }}
                      onClear={() => {
                        setDocumentUrl(null)
                        setDocumentPreviewUrl(null)
                        resetUpload()
                      }}
                    />
                  </Card.Section>
                </Card>

                {error && <RedAlert title={error} />}

                <Action.P
                  type="submit"
                  className="w-full"
                  performing={loading}
                  disabled={!!uploadProcessing}
                >
                  Save
                </Action.P>
              </Layout.Section>
            </Layout>
          </Form>
        )}
      </Page>
    </Frame>
  )
}

export default EditEnteredQuote
